import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchOne, fetchPaginated, api } from './helpers';
import type { PaginationParams } from './helpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DashboardKPIs {
  revenue_30d: number;
  revenue_trend: number;
  orders_30d: number;
  orders_trend: number;
  inventory_value: number;
  inventory_trend: number;
  gross_margin_pct: number;
  margin_trend: number;
  sales_trend: { date: string; revenue: number; orders: number }[];
  top_products: { product_name: string; sku: string; revenue: number; quantity: number }[];
  low_stock_alerts: { product_name: string; sku: string; on_hand: number; reorder_point: number }[];
  recent_orders: { order_number: string; customer_name: string; total: number; status: string; date: string }[];
  upcoming_deliveries: { po_number: string; vendor_name: string; expected_delivery: string; item_count: number }[];
  compliance_alerts: { type: string; message: string; severity: 'low' | 'medium' | 'high' | 'critical'; date: string }[];
}

export interface AgingBuckets { current: number; '30': number; '60': number; '90': number; '120_plus': number; }
export interface RevenueKPIs { total_revenue: number; revenue_per_day: number; order_count: number; avg_order_value: number; revenue_trend: number; period_days: number; }
export interface ARKPIs { total_outstanding: number; dso: number; aging_buckets: AgingBuckets; overdue_count: number; overdue_amount: number; }
export interface APKPIs { total_outstanding: number; dpo: number; aging_buckets: AgingBuckets; upcoming_due_7d: number; }
export interface InventoryKPIs { total_sku_count: number; total_valuation: number; low_stock_count: number; expiring_soon_count: number; spoilage_rate_30d: number; }
export interface OperationsKPIs { orders_shipped: number; fill_rate: number; on_time_delivery_pct: number; avg_pick_to_ship_hours: number; period_days: number; }
export interface HomeDashboardData { revenue: RevenueKPIs; ar: ARKPIs; ap: APKPIs; inventory: InventoryKPIs; operations: OperationsKPIs; generated_at: string; }

export interface SalesAnalyticsSummary {
  period_days: number; revenue: number; revenue_trend: number; order_count: number; order_trend: number; avg_order_value: number;
  top_customers: { id: number; name: string; code: string; order_count: number; total_revenue: number }[];
  daily_trend: { date: string; orders: number; revenue: number }[];
  status_breakdown: { status: string; count: number }[];
}
export interface PipelineSummary { stages: { stage: string; count: number; total_value: number; avg_probability: number }[]; total_opportunities: number; won: number; lost: number; win_rate: number; weighted_pipeline: number; }
export interface RepPerformance { period_days: number; reps: { rep: string; order_count: number; revenue: number; avg_deal_size: number }[]; }

export interface FieldVisit {
  id: number; salesperson_id: string; customer_id: number; customer_name: string; visit_date: string; visit_type: string;
  check_in_time: string | null; check_out_time: string | null; notes: string | null; orders_placed: number; total_sales: number;
}

export interface GlobalSearchResult { id: number | string; type: string; title: string; subtitle?: string; score?: number; }
export interface GlobalSearchResponse { results: GlobalSearchResult[]; total: number; }

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useDashboardKPIs() {
  return useQuery({
    queryKey: ['dashboard-kpis'],
    queryFn: () => fetchOne<DashboardKPIs>('/dashboard/kpis'),
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useHomeDashboard(days: number = 30) {
  return useQuery({
    queryKey: ['home-dashboard', days],
    queryFn: () => fetchOne<HomeDashboardData>(`/kpi/dashboard?days=${days}`),
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useSalesAnalytics(periodDays = 30) {
  return useQuery({
    queryKey: ['sales-analytics', periodDays],
    queryFn: () => fetchOne<SalesAnalyticsSummary>(`/sales/analytics/summary?period_days=${periodDays}`),
  });
}

export function usePipelineSummary() {
  return useQuery({
    queryKey: ['pipeline-summary'],
    queryFn: () => fetchOne<PipelineSummary>('/sales/analytics/pipeline'),
  });
}

export function useRepPerformance(periodDays = 30) {
  return useQuery({
    queryKey: ['rep-performance', periodDays],
    queryFn: () => fetchOne<RepPerformance>(`/sales/analytics/rep-performance?period_days=${periodDays}`),
  });
}

export function useFieldVisits(params?: PaginationParams & { salesperson_id?: string; customer_id?: number; visit_type?: string; date_from?: string; date_to?: string }) {
  return useQuery({ queryKey: ['field-visits', params], queryFn: () => fetchPaginated<FieldVisit>('/sales/field-visits', params) });
}

export function useCreateFieldVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { customer_id: number; visit_date: string; visit_type: string; check_in_time?: string; notes?: string; orders_placed?: number; total_sales?: number }) =>
      api.post('/sales/field-visits', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['field-visits'] }),
  });
}

export function useUpdateFieldVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; check_out_time?: string; notes?: string; orders_placed?: number; total_sales?: number }) =>
      api.patch(`/sales/field-visits/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['field-visits'] }),
  });
}

export function useGlobalSearch(query: string) {
  return useQuery({
    queryKey: ['global-search', query],
    queryFn: () => api.get<GlobalSearchResponse>('/search/global', { params: { q: query, limit: 15 } }).then(r => r.data),
    enabled: query.length >= 2,
    staleTime: 10_000,
  });
}

export function useMyDay() {
  return useQuery({ queryKey: ['my-day'], queryFn: () => api.get('/ai-assistant/my-day').then(r => r.data), staleTime: 60_000 });
}
export function useRefreshMyDay() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api.post('/ai-assistant/my-day/refresh').then(r => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['my-day'] }) });
}
export function useCompletePlanItem() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (itemId: string) => api.post(`/ai-assistant/my-day/items/${itemId}/complete`).then(r => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['my-day'] }) });
}
export function useSnoozePlanItem() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ itemId, until_date }: { itemId: string; until_date: string }) => api.post(`/ai-assistant/my-day/items/${itemId}/snooze`, { until_date }).then(r => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['my-day'] }) });
}
export function useDismissPlanItem() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (itemId: string) => api.post(`/ai-assistant/my-day/items/${itemId}/dismiss`).then(r => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['my-day'] }) });
}
